<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<div>

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" :editMode="dashboards.editMode">
				<GlyphIcon :icon="GlyphIconName['globe']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:6px;margin-top:1px">{{ data.title }}<span v-if="collapsed">&nbsp;({{ settings.shortcuts.length }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<Button size="small" variant="text" class="header-button" @click="toggleCollapse()">
						<GlyphIcon :icon="GlyphIconName['chevron-up']" size="19" class="transition-transform" :class="{ 'upside-down': collapsed }" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="settings-box">
				<div>{{ t('shortcuts.panelName') }}</div>
				<InputText maxlength="64" size="small" fluid v-model="settings.title" :required="true" />

				<div class="mt-2">{{ t('shortcuts.shortcuts') }}</div>
				<div>
					<draggable :list="data.shortcuts" item-key="_id" class="list-group" ghost-class="ghost">
						<template #item="{ element }">
							<div class="flex justify-between items-center pl-1 pr-1 pb-2 gap-1" style="cursor:move">
								<i class="pi pi-bars mr-1 text-muted-color"></i>
								<Button size="small" severity="danger" class="delete-item-btn" style="margin-right:5px;padding:3px" @click="removeItem(element)">
									<GlyphIcon :icon="GlyphIconName['bin']" size="15" color="#fff" />
								</Button>
								<InputText :id="element._id" type="text" maxlength="64" size="small" :placeholder="t('shortcuts.name')" v-model="element.name" style="width:178px;margin-right:5px" />
								<InputText maxlength="32" :placeholder="t('shortcuts.number')" size="small" style="width:115px" v-model="element.number" />
							</div>
						</template>
					</draggable>
					<div v-if="settings.shortcuts.length === 0" class="text-center italic mb-1 p-1 text-sm text-muted-color" style="background:#e0e0e0;border: 1px solid var(--p-form-field-border-color);border-radius:5px">{{ t('shortcuts.noShortcuts') }}</div>
				</div>

				<Button fluid severity="info" size="small" @click="addItem()">{{ t('shortcuts.add') }}</Button>

				<div style="height:4px"></div>
			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode" class="view-box" :style="{'max-height': collapsed ? '0px': (settings.shortcuts.length * 40) + 'px'}">
				<div v-for="item in settings.shortcuts" v-bind:key="(item as Item)._id" class="mb-1">
					<Button size="small" fluid @click="call(item.number)" severity="secondary">
						<div style="font-weight:300;min-width: 0;" class="dotdotdot">{{ item.name }}</div>
					</Button>
				</div>
			</div>
		</div>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import SipClient from '@/utilities/SipClient'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import Draggable from 'vuedraggable'
import type { PbxAppDashboardItemShortcuts, PbxAppDashboardItemShortcutsItem } from '@/TcxApi/PbxApp'
import { ref } from 'vue'
import { randomString } from '@/utilities/Tools'
import { GlyphIconName } from '@/stores/GlyphIcons.types'

const ss = useSettingsStore()
const dashboards = useDashboardsStore()
const { t } = useI18n()
const api = useApiStore()
const sipClient = SipClient.instance

interface Props {
	data: PbxAppDashboardItemShortcuts
}

interface Item extends PbxAppDashboardItemShortcutsItem {
	_id: string
}

const props = defineProps<Props>()
const isDraggable = ref(false)
const settings = dashboards.dashboard?.items.find(item => item.column === props.data.column && item.position == props.data.position) as PbxAppDashboardItemShortcuts
const collapsed = ref<boolean>(ss.getGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`) || false)

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function removeItem(item: Item) {
	const index = settings.shortcuts.findIndex(sc => (sc as Item)._id === item._id)
	if (index !== -1) {
		settings.shortcuts.splice(index, 1)
	}
}

function addItem() {
	const id = randomString(32);

	(settings.shortcuts as Item[]).push({ _id: id, name: '', number: '' })

	setTimeout(() => {
		document.getElementById(id)?.focus()
	}, 100)
}

async function call(number: string) {
	if (!number.startsWith('+') && number.length > 7) { number = ss.pbxSettings.nationalPrefix + number }
	if (!ss.realPhoneId) { noPhoneSelected(); return }
	if (ss.settings.softphoneId) {
		sipClient.call(number)
	} else {
		try { await api.get(`/pbx/app/action/call?device=${ss.realPhoneId}&target=${encodeURIComponent(number)}`) }
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') })
		}
	}
}

function noPhoneSelected() {
	globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.noPhoneMessage') })
}

function toggleCollapse() {
	collapsed.value = !collapsed.value
	ss.setGeneric(`${ss.settings.dashboardId}-${props.data.type}-${props.data.column}-${props.data.position}-collapsed`, collapsed.value)
}

props.data.shortcuts.forEach(item => { (item as Item)._id = randomString(32) })
</script>

<style scoped>
	.settings-box {
		margin: 5px 10px;
	}
	.view-box {
		margin: 4px 4px;
		overflow: auto;
		transition: 400ms;
	}
	.ghost {
		opacity: 0.25;
	}
	.delete-employee-btn {
		padding: 0 2px 2px 2px;
		margin-right: 5px;
	}

</style>
